import { useMutation, useQueryClient } from "@tanstack/react-query";
import * as noteListsApi from "@/lib/api/noteLists";
import { queryKeys } from "@/lib/query-keys";
import type {
  NoteListWithNotes,
  NoteListSchema,
  PropertyDefinition,
  ViewConfig,
  SmartFillConfig,
  NoteListKind,
} from "@/lib/api/noteLists";

export const useCreateNoteList = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      name: string;
      description?: string;
      icon?: string;
      kind?: NoteListKind;
      schema?: NoteListSchema;
      viewConfig?: ViewConfig;
      smartFillEnabled?: boolean;
      smartFillConfig?: SmartFillConfig;
      parentId?: string | null;
    }) => noteListsApi.createNoteList(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.noteLists.all });
    },
  });
};

export const useUpdateNoteList = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: {
        name?: string;
        description?: string | null;
        schema?: NoteListSchema;
        viewConfig?: ViewConfig | null;
        smartFillEnabled?: boolean;
        smartFillConfig?: SmartFillConfig;
        parentId?: string | null;
      };
    }) => noteListsApi.updateNoteList(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.noteLists.all });
      queryClient.invalidateQueries({
        queryKey: queryKeys.noteLists.detail(id),
      });
    },
  });
};

export const useDeleteNoteList = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, cascade }: { id: string; cascade?: boolean }) =>
      noteListsApi.deleteNoteList(id, { cascade }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.noteLists.all });
    },
  });
};

export const useReorderNoteList = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      newSortOrder,
      newParentId,
    }: {
      id: string;
      newSortOrder: number;
      newParentId?: string | null;
    }) => noteListsApi.reorderNoteList(id, { newSortOrder, newParentId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.noteLists.all });
    },
  });
};

export const useAddNoteToList = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      listId,
      noteId,
      properties,
    }: {
      listId: string;
      noteId: string;
      properties?: Record<string, unknown>;
    }) => noteListsApi.addNoteToList(listId, { noteId, properties }),
    onSuccess: (_, { listId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.noteLists.detail(listId),
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.noteLists.all });
      // Also invalidate notes query so inbox updates (notes now have noteLists)
      queryClient.invalidateQueries({ queryKey: queryKeys.notes.all });
    },
  });
};

export const useUpdateNoteInList = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      listId,
      noteId,
      properties,
    }: {
      listId: string;
      noteId: string;
      properties: Record<string, unknown>;
    }) => noteListsApi.updateNoteInList(listId, noteId, { properties }),
    onMutate: async ({ listId, noteId, properties }) => {
      await queryClient.cancelQueries({
        queryKey: queryKeys.noteLists.detail(listId),
      });

      const previousList = queryClient.getQueryData<NoteListWithNotes>(
        queryKeys.noteLists.detail(listId),
      );

      if (previousList) {
        queryClient.setQueryData<NoteListWithNotes>(
          queryKeys.noteLists.detail(listId),
          {
            ...previousList,
            notes: previousList.notes.map((note) =>
              note.noteId === noteId
                ? {
                    ...note,
                    properties: { ...note.properties, ...properties },
                  }
                : note,
            ),
          },
        );
      }

      return { previousList };
    },
    onError: (_err, { listId }, context) => {
      if (context?.previousList) {
        queryClient.setQueryData(
          queryKeys.noteLists.detail(listId),
          context.previousList,
        );
      }
    },
  });
};

export const useRemoveNoteFromList = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ listId, noteId }: { listId: string; noteId: string }) =>
      noteListsApi.removeNoteFromList(listId, noteId),
    onSuccess: (_, { listId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.noteLists.detail(listId),
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.noteLists.all });
    },
  });
};

export const useReorderNoteInList = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      listId,
      noteId,
      sortOrder,
    }: {
      listId: string;
      noteId: string;
      sortOrder: number;
    }) => noteListsApi.reorderNoteInList(listId, noteId, { sortOrder }),
    onMutate: async ({ listId, noteId, sortOrder: newPosition }) => {
      await queryClient.cancelQueries({
        queryKey: queryKeys.noteLists.detail(listId),
      });

      const previousList = queryClient.getQueryData<NoteListWithNotes>(
        queryKeys.noteLists.detail(listId),
      );

      if (previousList) {
        const notes = [...previousList.notes];
        const oldIndex = notes.findIndex((n) => n.noteId === noteId);
        if (oldIndex !== -1) {
          const [removed] = notes.splice(oldIndex, 1);
          const targetPosition = Math.max(
            0,
            Math.min(newPosition, notes.length),
          );
          notes.splice(targetPosition, 0, removed);
          const reorderedNotes = notes.map((note, index) => ({
            ...note,
            sortOrder: index,
          }));
          queryClient.setQueryData<NoteListWithNotes>(
            queryKeys.noteLists.detail(listId),
            { ...previousList, notes: reorderedNotes },
          );
        }
      }

      return { previousList };
    },
    onError: (_err, { listId }, context) => {
      if (context?.previousList) {
        queryClient.setQueryData(
          queryKeys.noteLists.detail(listId),
          context.previousList,
        );
      }
    },
  });
};

export const useUpdateListSchema = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      listId,
      addProperty,
      renameProperty,
      removeProperty,
      reorderProperties,
    }: {
      listId: string;
      addProperty?: { name: string; definition: PropertyDefinition };
      renameProperty?: { oldName: string; newName: string };
      removeProperty?: { name: string };
      reorderProperties?: { orderedKeys: string[] };
    }) =>
      noteListsApi.updateListSchema(listId, {
        addProperty,
        renameProperty,
        removeProperty,
        reorderProperties,
      }),
    onSuccess: (_, { listId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.noteLists.detail(listId),
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.noteLists.all });
    },
  });
};
